"use client";

import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useStorageQuery } from "../../../../hooks/storageQueryProvider";
import { storageResultsPerPageOptions } from "../../../../types";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";

export default function FoldersPagination({
    totalNumberOfFolders,
}: {
    totalNumberOfFolders: number;
}) {
    const { foldersQuery, updateFoldersQuery } = useStorageQuery();
    const { dir } = useLocale();
    const PrevIcon = dir === "rtl" ? ChevronRight : ChevronLeft;
    const NextIcon = dir === "rtl" ? ChevronLeft : ChevronRight;

    const totalPages = Math.ceil(
        totalNumberOfFolders / foldersQuery.resultsPerPage
    );

    const allowPrev = foldersQuery.page > 1;
    const allowNext = foldersQuery.page < totalPages;

    return (
        <div className="mt-4 flex flex-row gap-6 items-center">
            <div className="flex flex-col gap-3 md:flex-row md:gap-6 md:items-center">
                <div className="flex flex-row gap-1 items-center text-sm">
                    {t(
                        "storage.folders-grid.pagination.results-per-page",
                        undefined,
                        undefined
                    )}
                    <Select
                        dir={dir}
                        value={foldersQuery.resultsPerPage.toString()}
                        onValueChange={(value: string) => {
                            updateFoldersQuery({
                                resultsPerPage: parseInt(value),
                                page: 1,
                            });
                        }}
                    >
                        <SelectTrigger className="w-fit text-sm h-9">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {storageResultsPerPageOptions.map((val) => (
                                <SelectItem
                                    className="text-sm"
                                    key={val}
                                    value={val.toString()}
                                >
                                    {val}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <p className="text-sm">
                    {t(
                        "storage.folders-grid.pagination.page-out-of-pages",
                        undefined,
                        { currentPage: foldersQuery.page, totalPages }
                    )}
                </p>
                <div className="flex flex-row gap-2">
                    <Button
                        className="flex flex-row items-center font-normal text-sm"
                        variant="outline"
                        size="sm"
                        disabled={!allowPrev}
                        onClick={() => {
                            updateFoldersQuery({
                                page: foldersQuery.page - 1,
                            });
                        }}
                    >
                        <PrevIcon className="size-4" />
                        {t(
                            "storage.folders-grid.pagination.prev",
                            undefined,
                            undefined
                        )}
                    </Button>
                    <Button
                        className="flex flex-row items-center font-normal text-sm"
                        variant="outline"
                        size="sm"
                        disabled={!allowNext}
                        onClick={() => {
                            updateFoldersQuery({
                                page: foldersQuery.page + 1,
                            });
                        }}
                    >
                        {t(
                            "storage.folders-grid.pagination.next",
                            undefined,
                            undefined
                        )}
                        <NextIcon className="size-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}
